import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SortConfig } from '@pages/Admin/Campaign/ui/types';
import {
  CallDto,
  CampaignCallListPaginatedControllerCallListPaginatedApiArg,
  apiService,
} from '@shared/services/apiService/apiService';

import { CallsFilterFormValue } from '../model/type';

const PAGE_LIMIT = 20;

const { useLazyQuery: useLazyCallsPaginatedQuery } =
  apiService.endpoints.campaignCallListPaginatedControllerCallListPaginated;

const buildFilterArgs = (params: CallsFilterFormValue) => ({
  statuses: params.statuses !== 'all' ? params.statuses : undefined,
  types: params.types !== 'all' ? params.types : undefined,
  start: params.start?.length ? params.start : undefined,
  end: params.end?.length ? params.end : undefined,
  numberSearch: params.numberSearch?.length ? params.numberSearch : undefined,
  callIdSearch: params.callIdSearch?.length ? params.callIdSearch : undefined,
  endedReason: params.endedReason !== 'all' ? params.endedReason : undefined,
});

const toApiSort = (
  sortConfig: SortConfig,
): Pick<CampaignCallListPaginatedControllerCallListPaginatedApiArg, 'sortBy' | 'sortOrder'> => ({
  sortBy: (sortConfig.key ?? 'createdAt') as NonNullable<
    CampaignCallListPaginatedControllerCallListPaginatedApiArg['sortBy']
  >,
  sortOrder: sortConfig.direction === 'asc' ? 'ASC' : 'DESC',
});

const mergeCallsById = (prev: CallDto[], next: CallDto[]): CallDto[] => {
  const uniqueById = new Map<string, CallDto>();
  [...prev, ...next].forEach((item) => uniqueById.set(item.id, item));
  return Array.from(uniqueById.values());
};

export const useGetCalls = (
  campaignId?: string,
  params: CallsFilterFormValue = {},
  sortConfig: SortConfig = { key: 'createdAt', direction: 'desc' },
) => {
  const [callsData, setCallsData] = useState<CallDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalItems, setTotalItems] = useState<number | undefined>(undefined);
  const lastRequestedCursorRef = useRef<string | null>(null);

  const [fetchCalls, { isFetching, isLoading, isError }] = useLazyCallsPaginatedQuery();

  const filterArgs = useMemo(
    () => buildFilterArgs(params),
    [
      params.statuses,
      params.types,
      params.start,
      params.end,
      params.numberSearch,
      params.callIdSearch,
      params.endedReason,
    ],
  );
  const apiSort = useMemo(() => toApiSort(sortConfig), [sortConfig]);

  const loadPage = useCallback(
    async (pageCursor?: string | null, shouldAppend = false) => {
      if (!campaignId) return;

      const normalizedCursor = pageCursor || null;
      if (shouldAppend && normalizedCursor && lastRequestedCursorRef.current === normalizedCursor) {
        return;
      }
      if (shouldAppend && normalizedCursor) {
        lastRequestedCursorRef.current = normalizedCursor;
      }

      const requestArgs: CampaignCallListPaginatedControllerCallListPaginatedApiArg =
        shouldAppend && normalizedCursor
          ? {
              id: campaignId,
              limit: PAGE_LIMIT,
              cursor: normalizedCursor,
              ...filterArgs,
              ...apiSort,
            }
          : {
              id: campaignId,
              limit: PAGE_LIMIT,
              withTotal: true,
              ...filterArgs,
              ...apiSort,
            };

      try {
        const response = await fetchCalls(requestArgs).unwrap();
        setCallsData((prev) =>
          shouldAppend ? mergeCallsById(prev, response.data) : response.data,
        );
        setCursor(response.meta.cursor);
        setHasMore(response.meta.hasMore);
        if (!shouldAppend) {
          setTotalItems(response.meta.totalItems);
        }
      } catch {
        if (shouldAppend) {
          lastRequestedCursorRef.current = null;
        } else {
          setCallsData([]);
          setCursor(null);
          setHasMore(false);
          setTotalItems(undefined);
        }
      }
    },
    [apiSort, campaignId, fetchCalls, filterArgs],
  );

  const recipientRefetch = useCallback(async () => {
    lastRequestedCursorRef.current = null;
    setCursor(null);
    setHasMore(false);
    setTotalItems(undefined);
    await loadPage(null, false);
  }, [loadPage]);

  const loadMore = useCallback(async () => {
    if (!hasMore || !cursor || isFetching || isLoading) return;
    await loadPage(cursor, true);
  }, [cursor, hasMore, isFetching, isLoading, loadPage]);

  useEffect(() => {
    if (!campaignId) {
      setCallsData([]);
      setCursor(null);
      setHasMore(false);
      return;
    }
    void recipientRefetch();
  }, [campaignId, filterArgs, apiSort, recipientRefetch]);

  return {
    callsData,
    hasMore,
    loadMore,
    totalItems,
    isErrorRecipient: isError,
    recipientRefetch,
    recipientIsLoading: isLoading,
    isFetching,
  };
};
